import { PubObject, calculateBeerMugRating } from "./FindPubs"; // Assuming calculateBeerMugRating is exported
import {
    Box,
    Typography,
    CircularProgress,
    Paper,
    Rating,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    useTheme,
    useMediaQuery,
    IconButton,
    Stack,
} from "@mui/material";
import IntensityTimeline from "./IntensityTimeline";
import { getPubRaycasting, getPubYearlySunPoints, getNearbyPubs, getPubWithNearby } from "../API";
import React, { useState, useEffect, useMemo, lazy, Suspense, useCallback, useRef } from "react";
import { themeColors } from "../styles/theme"; // Import theme for CSS vars
import Fonts from './Fonts';
import { useNavigate, useLocation, useParams } from "react-router-dom";
import AddIcon from '@mui/icons-material/Add';
import { styled } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ShareModal from './ShareModal';
import ShareIcon from '@mui/icons-material/Share';
import MultiMeshViewer from './MultiMeshViewer';
import YearlySunChart from './YearlySunChart';
import {
    trackPubView,
    trackNoGardenBounce,
    trackGardenViewDuration,
    trackGardenEditStarted,
    trackShareInitiated,
    trackReturnToSearch,
    track3DSceneLoadingTime
} from "../utils/analytics";
import NearbyPubs from './NearbyPubs';
import { extractCity } from '../utils/cityExtractor';
import { SunIcon } from "./SunIcon";
import { Footer } from './Footer';
import { BottomSheet, BottomSheetRef } from 'react-spring-bottom-sheet'; // <-- Import BottomSheet
import 'react-spring-bottom-sheet/dist/style.css'; // <-- Import BottomSheet CSS
import { MouseIcon } from './Icons';

// Lazy load the 3D viewer component
const ShadedVertexViewer = lazy(() => import('./ViewSunlight3D'));

// --- Interfaces (Copied from original) ---
interface HeatmapData { timestamps: string[]; values: number[]; }
interface IntensityData { timestamps: string[]; values: { [googlePlaceId: string]: number[]; }; }
interface Garden { name: string; uid: string; coords: Array<[number, number]>; raycastResults: { [timestamp: string]: number[]; }; averageSun: number[]; }
interface PubRaycastingResponse { pub_id: string; date: string; original_query_date: string; status?: string; message?: string; garden_names?: Record<string, string>; garden_details?: { [garden_uid: string]: { name: string; is_uncertain: boolean; } }; has_no_garden?: boolean; raycast_results?: any; garden_coords?: any; sun_times?: { sunrise: string; sunset: string; }; above_horizon_steps?: number; average_sun?: number[]; heatmap_1d?: HeatmapData; mesh_uris?: string[]; centre_ecef?: [number, number, number]; session_id?: string; copyrights?: string[]; sun_vector?: { [timestamp: string]: [number, number, number] }; garden_polygons?: { [garden_uid: string]: { polygon: [number, number][]; elevation: number | null; } }; [gardenId: string]: any; city?: string; latitude?: number; longitude?: number; }
// Basic interface for the yearly sun points response based on original usage
interface PubYearlySunPoints {
    status: 'success' | 'no_data' | 'error';
    dates: string[];
    sun_percentages: number[];
    message?: string;
}
// --- End Interfaces ---

// --- Utility Functions (Copied from original) ---
const latLonElevToECEF = (lat: number, lon: number, elevation: number): [number, number, number] => {
    const latRad = (lat * Math.PI) / 180;
    const lonRad = (lon * Math.PI) / 180;
    const a = 6378137.0;
    const f = 1/298.257223563;
    const e2 = 2*f - f*f;
    const N = a / Math.sqrt(1 - e2 * Math.sin(latRad) * Math.sin(latRad));
    const X = (N + elevation) * Math.cos(latRad) * Math.cos(lonRad);
    const Y = (N + elevation) * Math.cos(latRad) * Math.sin(lonRad);
    const Z = (N * (1 - e2) + elevation) * Math.sin(latRad);
    return [X, Y, Z];
};

const convertToIntensityData = (data: HeatmapData | undefined, id: string): IntensityData => {
    if (!data || !data.timestamps || !data.values) {
        return { timestamps: [], values: { [id]: [] } };
    }
    return { timestamps: data.timestamps, values: { [id]: data.values } };
};

const SubmitGardenButton = styled(Button)(({ theme }) => ({
    textTransform: 'none',
    fontWeight: 500,
    borderRadius: '12px',
    padding: '10px 20px',
    background: '#4CAF50', // TODO: Use theme color if available
    color: '#fff',
    boxShadow: '0 4px 10px rgba(76, 175, 80, 0.3)',
    '&:hover': {
        background: '#45a049',
        boxShadow: '0 6px 14px rgba(76, 175, 80, 0.4)',
    },
}));

interface ViewPubProps {
    pubToView?: PubObject;
    googlePlaceId?: string;
}

// --- Constants for Bottom Sheet ---
const PEEK_HEIGHT_NUM = 220; // Increased height to fit name, buttons, and details
const PEEK_HEIGHT_EXPANDED = 110;
const MAX_SHEET_HEIGHT_VH = 85; // vh unit for max height
const SHEET_BORDER_RADIUS = '16px'; // Top border radius
const MAX_SHEET_WIDTH = '700px'; // Max width for the sheet on desktop

// --- Style for the buttons within the header ---
const HeaderButtonStyle = {
    textTransform: 'none',
    fontWeight: 500,
    borderRadius: '48px', // Match Home.tsx PrimaryButton
    padding: '6px 16px', // Slightly smaller padding for header bar
    fontSize: '0.9rem',   // Slightly smaller font
    background: 'transparent',
    color: '#000', // Black text
    border: `1.5px solid black`, // Black outline style
    '&:hover': {
        background: 'rgba(0, 0, 0, 0.05)', // Subtle hover
        borderColor: 'black', // Keep border color
    },
    // Ensure buttons don't get too wide and have some space
    minWidth: '90px', // Adjusted min width
    flexShrink: 0,
    flexGrow: 1, // Allow buttons to grow slightly if needed
    textAlign: 'center',
};

const ViewPub = ({ pubToView: propPubToView, googlePlaceId: propGooglePlaceId }: ViewPubProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { googlePlaceId: urlPubId } = useParams<{ googlePlaceId: string }>();
    const muiTheme = useTheme(); // Use muiTheme alias
    const isMobile = useMediaQuery('(max-width:600px)');
    const timelineMode = 'compact'; // Always use compact mode

    const effectiveGooglePlaceId = urlPubId || propGooglePlaceId;

    // State declarations (Copied from original)
    const [pubData, setPubData] = useState<PubObject | null>(null);
    const [raycastingData, setRaycastingData] = useState<PubRaycastingResponse | null>(null);
    const [isLoadingPub, setIsLoadingPub] = useState<boolean>(true);
    const [isLoadingRaycast, setIsLoadingRaycast] = useState<boolean>(false);
    const [isLoadingYearly, setIsLoadingYearly] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [submittedGarden, setSubmittedGarden] = useState<boolean>(false);
    const [sceneLoadStartTime, setSceneLoadStartTime] = useState<number | null>(null);
    const [nearbyPubsData, setNearbyPubsData] = useState<any[]>([]);
    const [attributionOpen, setAttributionOpen] = useState<boolean>(false);
    const [shareModalOpen, setShareModalOpen] = useState<boolean>(false);
    const [hasUserLocation, setHasUserLocation] = useState<boolean>(false);
    const [userDistance, setUserDistance] = useState<number | undefined>(undefined);
    const [yearlySunData, setYearlySunData] = useState<any[]>([]);
    const [gardenSunDetails, setGardenSunDetails] = useState<{ [gardenId: string]: { name: string; color: string; } }>({});
    const [yearlyDataError, setYearlyDataError] = useState<string | null>(null);
    const [isSheetExpanded, setIsSheetExpanded] = useState<boolean>(false); // Controls BottomSheet 'open' state indirectly

    // Refs (Copied from original)
    const pageLoadTimeRef = useRef<number>(Date.now());
    const timeOnPageRef = useRef<number>(0);
    const raycastingFetchedRef = useRef<boolean>(false);
    const yearlyDataFetchedRef = useRef<boolean>(false);
    const sheetRef = useRef<BottomSheetRef>(null); // Ref for BottomSheet programmatic control (optional)


    // =============================
    // == useEffect Hooks Section ==
    // =============================
    // (No changes needed in useEffects for this refactor)

    // == Effect for Initial Data Loading, Resetting ==
    useEffect(() => {
        setError(null);
        setIsLoadingPub(true);
        setIsLoadingRaycast(false);
        setIsLoadingYearly(false);
        setRaycastingData(null);
        setYearlySunData([]);
        setNearbyPubsData([]);
        setPubData(null);
        raycastingFetchedRef.current = false;
        yearlyDataFetchedRef.current = false;
        setIsSheetExpanded(false); // Collapse sheet on navigation
        sheetRef.current?.snapTo(({ snapPoints }) => snapPoints[0]); // Ensure visually collapsed
        pageLoadTimeRef.current = Date.now();

        const locationStatePub = location.state?.pubToView as PubObject | undefined;
        const pubIdToLoad = effectiveGooglePlaceId;

        const fetchNearbyIfNeeded = (lat?: number, lon?: number) => {
            if (lat && lon && nearbyPubsData.length === 0) {
                getNearbyPubs(lat, lon, 2000)
                    .then(response => {
                        if (response.data.pubs?.length > 0) {
                            setNearbyPubsData(response.data.pubs);
                        }
                    })
                    .catch(err => console.error("Error fetching nearby pubs separately:", err));
            }
        };

        if (locationStatePub && locationStatePub.google_place_id === pubIdToLoad) {
             if (!locationStatePub.city && locationStatePub.address) {
                 locationStatePub.city = extractCity(locationStatePub.address) || undefined;
             }
            setPubData(locationStatePub);
            fetchNearbyIfNeeded(locationStatePub.latitude, locationStatePub.longitude);
            setIsLoadingPub(false);
            // Update trackPubView to include all 4 required arguments
            trackPubView(
                locationStatePub.google_place_id,
                locationStatePub.name,
                !!(locationStatePub.garden_drawn && !locationStatePub.has_no_garden),
                !!locationStatePub.has_no_garden
            );
        } else if (pubIdToLoad) {
            getPubWithNearby(pubIdToLoad)
                .then(response => {
                    if (response.data?.pub) {
                        const pubDetails = response.data.pub as PubObject;
                        if (!pubDetails.city && pubDetails.address) {
                            pubDetails.city = extractCity(pubDetails.address) || undefined;
                        }
                        setPubData(pubDetails);
                        if (response.data.nearby_pubs?.length > 0) {
                            setNearbyPubsData(response.data.nearby_pubs);
                        } else {
                            fetchNearbyIfNeeded(pubDetails.latitude, pubDetails.longitude);
                        }
                         // Update trackPubView to include all 4 required arguments
                         trackPubView(
                             pubDetails.google_place_id,
                             pubDetails.name,
                             !!(pubDetails.garden_drawn && !pubDetails.has_no_garden),
                             !!pubDetails.has_no_garden
                         );
                    } else {
                        setError("We couldn't find this pub. It may have been removed or the link is incorrect.");
                    }
                })
                .catch(error => {
                    console.error("Error fetching pub with nearby:", error);
                    if (error.response && error.response.status === 404) {
                        setError("We couldn't find this pub. It may have been removed or the link is incorrect.");
                    } else {
                        setError("Error loading pub data. Please try again.");
                    }
                })
                .finally(() => {
                    setIsLoadingPub(false);
                });
        } else {
            setError("No pub specified. Please return to search.");
            setIsLoadingPub(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, effectiveGooglePlaceId]);

    // == Effect for User Location and Distance Calculation ==
    useEffect(() => {
        const locationPermission = localStorage.getItem('locationPermissionGranted');
        const storedLat = localStorage.getItem('userLatitude');
        const storedLon = localStorage.getItem('userLongitude');
        const hasValidLocation = Boolean(locationPermission === 'true' && storedLat && storedLon);
        setHasUserLocation(hasValidLocation);

        if (hasValidLocation && pubData?.latitude && pubData?.longitude) {
            try {
                const userLat = parseFloat(storedLat!); const userLon = parseFloat(storedLon!);
                const R = 6371e3; // Earth radius in meters
                const φ1 = userLat * Math.PI / 180; const φ2 = pubData.latitude * Math.PI / 180;
                const Δφ = (pubData.latitude - userLat) * Math.PI / 180; const Δλ = (pubData.longitude - userLon) * Math.PI / 180;
                const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
                const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
                const distance = R * c;
                setUserDistance(distance);
            } catch (e) { console.error("Error calculating distance:", e); setUserDistance(undefined); }
        } else {
            setUserDistance(undefined);
        }
    }, [pubData?.latitude, pubData?.longitude]);

    // == Effect to control BottomSheet snap based on isSheetExpanded state ==
    useEffect(() => {
        if (sheetRef.current) {
            // Snap to max height if expanded, otherwise snap to min (peek) height
            sheetRef.current.snapTo(({ snapPoints }) =>
                isSheetExpanded ? Math.max(...snapPoints) : Math.min(...snapPoints)
            );
        }
    }, [isSheetExpanded]); // Run whenever isSheetExpanded changes

    // == Effect to Fetch Raycasting Data ==
    useEffect(() => {
        if (raycastingFetchedRef.current || isLoadingRaycast || isLoadingPub || !pubData?.google_place_id) {
            return;
        }
        const fetchRaycastingData = async () => {
            setIsLoadingRaycast(true);
            raycastingFetchedRef.current = true;
            try {
                const response = await getPubRaycasting(pubData.google_place_id, true);
                const data = response.data as PubRaycastingResponse;
                if (data.status === 'error') {
                     throw new Error(data.message || 'Raycasting failed');
                }
                 const updates: Partial<PubObject> = {};
                 if (!pubData.city && data.city) updates.city = data.city;
                 const definitelyNoGarden = data.status === 'no_garden' || data.has_no_garden === true;
                 if (definitelyNoGarden && !pubData.has_no_garden) updates.has_no_garden = true;
                 // Ensure garden_drawn status aligns with raycasting result if not already set
                 if (!definitelyNoGarden && data.garden_details && Object.keys(data.garden_details).length > 0 && !pubData.garden_drawn) {
                     updates.garden_drawn = true;
                     updates.has_no_garden = false; // Explicitly set false if drawn
                 } else if (definitelyNoGarden && pubData.garden_drawn) {
                      updates.garden_drawn = false; // Mark as not drawn if backend says no garden
                 }

                 if (Object.keys(updates).length > 0) {
                     setPubData(prev => prev ? { ...prev, ...updates } : null);
                 }
                setRaycastingData(data);
                setError(null); // Clear error on success
            } catch (err: any) {
                console.error("Error fetching/processing raycasting data:", err);
                setError(prevError => prevError || `Sun data unavailable. ${err.message || 'Please try again later.'}`);
                setRaycastingData(null);
                // Decide if refetch should be allowed on error
                 raycastingFetchedRef.current = false; // Maybe allow refetch? Or require manual refresh?
            } finally {
                setIsLoadingRaycast(false);
            }
        };
        fetchRaycastingData();
    }, [pubData, isLoadingPub, isLoadingRaycast]);


    // == Effect to Fetch Yearly Sun Points Data ==
    useEffect(() => {
        if (yearlyDataFetchedRef.current || isLoadingYearly || isLoadingPub || !pubData?.google_place_id || isLoadingRaycast || !raycastingData?.garden_details) {
            return;
        }
        const fetchYearlySunPointsData = async () => {
            setIsLoadingYearly(true);
            setYearlyDataError(null);
            yearlyDataFetchedRef.current = true;
            try {
                 // Use original handling logic for yearly data
                const response = (await getPubYearlySunPoints(pubData.google_place_id)) as PubYearlySunPoints;
                if (response.status === 'success' && response.dates?.length > 0 && response.sun_percentages?.length > 0) {
                    const gardens = raycastingData.garden_details || {};
                    const gardenIds = Object.keys(gardens);
                    const gardenColors: { [gardenId: string]: { name: string; color: string } } = {};
                    const GARDEN_COLORS = [ themeColors.orange, themeColors.blue, '#4CAF50', '#9C27B0', '#FF5722', '#2196F3', '#795548', '#607D8B' ];
                    gardenIds.forEach((gardenId, index) => {
                        gardenColors[gardenId] = { name: gardens[gardenId].name || `Garden ${gardenId.slice(0,8)}`, color: GARDEN_COLORS[index % GARDEN_COLORS.length] };
                    });
                    setGardenSunDetails(gardenColors);

                    const chartData = response.dates.map((date: string, dateIndex: number) => {
                         const dataPoint: any = { date };
                         const basePercentage = (response.sun_percentages[dateIndex] ?? 0) * 100; // Use nullish coalescing for safety

                         if (gardenIds.length === 1) {
                             dataPoint[gardenIds[0]] = basePercentage;
                         } else if (gardenIds.length > 1) {
                              // Reverted to original simple variation logic
                             gardenIds.forEach((gardenId, gardenIndex) => {
                                 const variation = Math.sin(dateIndex * 0.2 + gardenIndex) * 5; // Simple variation
                                 dataPoint[gardenId] = Math.min(100, Math.max(0, basePercentage + variation));
                             });
                         } else {
                             // Original fallback
                             dataPoint.value = basePercentage;
                         }
                         return dataPoint;
                     });
                    setYearlySunData(chartData);
                    setYearlyDataError(null); // Clear error on success
                } else if (response.status === 'no_data') {
                    setYearlyDataError('No yearly sun data available yet.');
                    setYearlySunData([]);
                } else {
                     setYearlyDataError(response.message || 'Could not retrieve yearly sun data.');
                     setYearlySunData([]);
                }
            } catch (err: any) {
                console.error('Error fetching yearly sun data:', err);
                setYearlyDataError(err.message || 'Failed to fetch yearly sun data');
                setYearlySunData([]);
                yearlyDataFetchedRef.current = false; // Maybe allow retry
            } finally {
                setIsLoadingYearly(false);
            }
        };
        fetchYearlySunPointsData();
    }, [pubData?.google_place_id, raycastingData, isLoadingPub, isLoadingRaycast, isLoadingYearly]);


    // == Memoized Calculation for Garden Data ==
     // Moved *after* state/refs, *before* dependent calculations/effects
    const gardensData = useMemo<Garden[]>(() => {
        if (!raycastingData) return [];
        try {
            const validGardenIds = raycastingData.garden_details ? Object.keys(raycastingData.garden_details) : [];
            const processedGardens = validGardenIds.map(gardenId => {
                const gardenObject = raycastingData[gardenId];
                 if (!gardenObject) return null;
                try {
                    const gardenName = raycastingData.garden_details?.[gardenId]?.name || `Garden ${gardenId.slice(0, 8)}`;
                     const coords3D = gardenObject.garden_vertex_coords || raycastingData.garden_coords?.[gardenId];
                    if (!coords3D || !Array.isArray(coords3D) || coords3D.length === 0) return null;
                    const coords2D = coords3D.map((coord: number[]) => {
                        if (Array.isArray(coord) && coord.length >= 2) return [coord[0], coord[1]] as [number, number];
                        return null;
                    }).filter((coord): coord is [number, number] => coord !== null);
                    if (coords2D.length === 0) return null;
                    let raycastResults = gardenObject.hourly_data || {};
                    let averageSun: number[] = gardenObject.heatmap_1d?.values || [];
                    return { name: gardenName, uid: gardenId, coords: coords2D, raycastResults, averageSun } as Garden;
                } catch (parseErr) {
                    console.error(`Error parsing data for garden ${gardenId}:`, parseErr);
                    return null;
                }
            });
            return processedGardens.filter((garden): garden is Garden => garden !== null);
        } catch (err) {
            console.error("Error processing gardensData:", err);
            return [];
        }
    }, [raycastingData]);


    // == Effect to Calculate Rating based on Processed gardensData ==
     // Depends on gardensData, so placed after its calculation
    useEffect(() => {
        if (!isLoadingRaycast && raycastingData && gardensData.length > 0 && pubData && !pubData.rating) {
            let totalPoints = 0;
            let gardensWithData = 0;
            gardensData.forEach(garden => {
                if (garden?.averageSun?.length > 0) {
                    const gardenPoints = garden.averageSun.reduce((sum, val) => sum + val, 0);
                    totalPoints += gardenPoints;
                    gardensWithData++; // Count gardens that actually contributed data
                }
            });

            if (gardensWithData > 0) {
                // Average points *per garden* that had data
                const averagePointsPerGarden = totalPoints / gardensWithData;
                const rating = calculateBeerMugRating(averagePointsPerGarden); // Use original calculation logic if intended
                setPubData(prev => prev ? { ...prev, rating } : null);
            } else if (gardensData.length > 0) {
                 console.warn("Gardens defined, but no averageSun data found to calculate rating.");
            }
        }
    }, [isLoadingRaycast, raycastingData, gardensData, pubData]); // Keep dependencies

    // == Memoized calculation for Garden Heatmaps ==
     // Depends on raycastingData
    const gardenHeatmaps = useMemo(() => {
        if (!raycastingData) return {};
        try {
            const result: Record<string, HeatmapData> = {};
             const validGardenIds = raycastingData.garden_details ? Object.keys(raycastingData.garden_details) : [];
             validGardenIds.forEach(gardenId => {
                 const gardenData = raycastingData[gardenId];
                 if (gardenData?.heatmap_1d?.timestamps && Array.isArray(gardenData.heatmap_1d.values)) {
                     result[gardenId] = gardenData.heatmap_1d;
                 }
             });
            return result;
        } catch (err) {
            console.error("Error processing gardenHeatmaps:", err);
            return {};
        }
    }, [raycastingData]);

    // == Memoized processing for 3D Viewer Data ==
     // Depends on raycastingData
    interface ProcessedGarden3D { uid: string; name: string; ecefCoordinates: Array<[number, number, number]>; raycastResults: { [timestamp: string]: number[] }; }
    const { dynamicMeshUris, dynamicFocalPoint, gardenDataFor3D } = useMemo(() => {
        const defaultReturn = { dynamicMeshUris: [] as string[], dynamicFocalPoint: [0,0,0] as [number, number, number], gardenDataFor3D: [] as ProcessedGarden3D[] };
        if (!raycastingData) return defaultReturn;
        try {
            const googleTilesApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
            const baseUrl = 'https://tile.googleapis.com';
            const meshUris = raycastingData.mesh_uris || [];
            const sessionId = raycastingData.session_id || '';
            const centreEcef = raycastingData.centre_ecef || null;

            const completeUrls = meshUris.map(uri => {
                const uriPath = uri.startsWith('/') ? uri : `/${uri}`;
                const cleanSessionId = sessionId.startsWith('session=') ? sessionId.substring(8) : sessionId;
                if (!sessionId || !googleTilesApiKey) {
                     console.warn("Missing sessionId or API key for mesh URL generation.");
                     return '';
                 }
                return `${baseUrl}${uriPath}?session=${cleanSessionId}&key=${googleTilesApiKey}`;
            }).filter(url => url !== ''); // Filter out empty URLs if key/session missing

            let focalPoint = defaultReturn.dynamicFocalPoint;
            if (centreEcef && Array.isArray(centreEcef) && centreEcef.length === 3 && centreEcef.every(n => typeof n === 'number')) {
                focalPoint = centreEcef as [number, number, number];
            } else if (pubData?.latitude && pubData?.longitude) {
                 focalPoint = latLonElevToECEF(pubData.latitude, pubData.longitude, 50); // Original fallback
                 console.warn("Using pub lat/lon + 50m elevation for 3D focal point due to missing/invalid centre_ecef");
            }

            const validGardenIds = raycastingData.garden_details ? Object.keys(raycastingData.garden_details) : [];
            let processedGardenData: ProcessedGarden3D[] = [];
            validGardenIds.forEach(gardenId => {
                 const gardenObject = raycastingData[gardenId];
                 if (!gardenObject) return;
                const coords3D = gardenObject.garden_vertex_coords;
                if (coords3D && Array.isArray(coords3D) && coords3D.length > 0) {
                    const ecefCoords = coords3D.map((coord: number[]) => {
                        // Assuming coords3D are [lat, lon, elev]
                        if (Array.isArray(coord) && coord.length >= 3 && coord.every(n => typeof n === 'number')) {
                            return latLonElevToECEF(coord[0], coord[1], coord[2]);
                        }
                        return null;
                    }).filter((coord): coord is [number, number, number] => coord !== null);
                    if (ecefCoords.length === 0) return;
                    const hourlyData = gardenObject.hourly_data || {};
                    const gardenName = raycastingData.garden_details?.[gardenId]?.name || `Garden ${gardenId.slice(0, 8)}`;
                    processedGardenData.push({ uid: gardenId, name: gardenName, ecefCoordinates: ecefCoords, raycastResults: hourlyData });
                }
            });
            return { dynamicMeshUris: completeUrls, dynamicFocalPoint: focalPoint, gardenDataFor3D: processedGardenData };
        } catch (err) {
            console.error("Error processing garden data for 3D view:", err);
            return defaultReturn;
        }
    }, [raycastingData, pubData?.latitude, pubData?.longitude]); // Keep original dependencies

    // == Analytics Tracking Effects (Copied from original) ==
    useEffect(() => {
        if (!pubData || isLoadingPub) return;
        let bounceTracked = false;
        const handleBeforeUnload = () => {
            if (!bounceTracked && pubData && !pubData.garden_drawn && !pubData.has_no_garden && !submittedGarden) {
                 timeOnPageRef.current = (Date.now() - pageLoadTimeRef.current) / 1000;
                 if (timeOnPageRef.current > 1.5) {
                    trackNoGardenBounce(pubData.google_place_id, timeOnPageRef.current);
                    bounceTracked = true;
                 }
             }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
             if (!bounceTracked && pubData && !pubData.garden_drawn && !pubData.has_no_garden && !submittedGarden) {
                 timeOnPageRef.current = (Date.now() - pageLoadTimeRef.current) / 1000;
                 if (timeOnPageRef.current > 1.5) {
                     trackNoGardenBounce(pubData.google_place_id, timeOnPageRef.current);
                 }
             }
        };
    }, [pubData, isLoadingPub, submittedGarden]); // Original dependencies

    useEffect(() => {
         // Depends on gardensData, placed after its calculation
        if (pubData && !isLoadingRaycast && gardensData.length > 0) {
             const startTime = Date.now();
             return () => {
                 if (pubData) {
                    trackGardenViewDuration(pubData.google_place_id, gardensData.length, (Date.now() - startTime) / 1000);
                 }
             };
        }
         return () => {};
    }, [pubData, isLoadingRaycast, gardensData]); // Original dependencies

     useEffect(() => {
          // Depends on dynamicMeshUris, placed after its calculation
         if (dynamicMeshUris.length > 0 && sceneLoadStartTime && pubData) {
             const timer = setTimeout(() => {
                 const loadTime = (Date.now() - sceneLoadStartTime) / 1000;
                 track3DSceneLoadingTime(loadTime, pubData.google_place_id, dynamicMeshUris.length);
             }, 4000);
             return () => clearTimeout(timer);
         }
          return () => {}; // Added missing cleanup return for consistency
     }, [dynamicMeshUris, sceneLoadStartTime, pubData]); // Original dependencies

     useEffect(() => {
          // Depends on dynamicMeshUris, placed after its calculation
         if (dynamicMeshUris.length > 0 && !sceneLoadStartTime) {
             setSceneLoadStartTime(Date.now());
         } else if (dynamicMeshUris.length === 0 && sceneLoadStartTime) {
             setSceneLoadStartTime(null);
         }
     }, [dynamicMeshUris, sceneLoadStartTime]); // Original dependencies


    // == SEO Updates Effect (Copied from original) ==
    useEffect(() => {
        if (pubData && !isLoadingPub) {
            let cityToUse = pubData.city;
            if (!cityToUse && pubData.address) {
                cityToUse = extractCity(pubData.address) || undefined;
            }
            const effectiveCity = cityToUse || 'London';
            document.title = `${pubData.name} - Sunny Pub Garden in ${effectiveCity} | Suntap`;
            const metaDescription = document.querySelector('meta[name="description"]');
            if (metaDescription) metaDescription.setAttribute('content', `Find the sunniest spots at ${pubData.name} in ${effectiveCity}. Real-time sun tracking shows exactly where and when this pub garden will be in sunshine.`);
            const canonicalLink = document.querySelector('link[rel="canonical"]');
            if (canonicalLink) canonicalLink.setAttribute('href', `https://sun-tap.com/pub/${pubData.google_place_id}`);
            const scriptTag = document.getElementById('pub-json-ld');
            if (scriptTag) {
                try {
                    scriptTag.textContent = JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "LocalBusiness",
                        "name": pubData.name,
                        "address": { "@type": "PostalAddress", "addressLocality": effectiveCity, "streetAddress": pubData.address || "" },
                        "geo": { "@type": "GeoCoordinates", "latitude": pubData.latitude, "longitude": pubData.longitude },
                        "url": `https://sun-tap.com/pub/${pubData.google_place_id}`,
                        "image": "https://sun-tap.com/web-app-manifest-512x512.png",
                        "description": `Find the sunniest spots at ${pubData.name} in ${effectiveCity}. Our 3D sun tracker shows exactly which parts of this garden are in sunshine throughout the day.`,
                        "areaServed": effectiveCity,
                        "servesCuisine": "Pub Food",
                        ...(pubData.rating && pubData.rating > 0 && { // Added rating from original logic
                           "aggregateRating": {
                             "@type": "AggregateRating",
                             "ratingValue": pubData.rating.toFixed(1),
                             "bestRating": "5",
                             "worstRating": "1",
                             "ratingCount": 1 // Placeholder
                           }
                        })
                    });
                } catch (e) {
                    console.error("Failed to stringify JSON-LD", e);
                }
            }
        } else if (!isLoadingPub) {
            document.title = 'Suntap - Find Sunny Pub Gardens';
            const metaDescription = document.querySelector('meta[name="description"]');
            if (metaDescription) metaDescription.setAttribute('content', 'Discover the best sunny pub gardens near you. Suntap uses 3D mapping to show you exactly where the sun will be.');
            const canonicalLink = document.querySelector('link[rel="canonical"]');
            if (canonicalLink) canonicalLink.setAttribute('href', `https://sun-tap.com${location.pathname}`);
            const scriptTag = document.getElementById('pub-json-ld');
            if (scriptTag) scriptTag.textContent = '{}';
        }
    }, [pubData, isLoadingPub, location.pathname]);

    // ============================
    // == Derived Booleans Section ==
    // ============================
    // Moved *after* state/refs/memos they depend on, *before* render functions/JSX
    const isMarkedNoGarden = pubData?.has_no_garden === true || (raycastingData?.has_no_garden === true) || (raycastingData?.status === 'no_garden');
    const hasDrawableGarden = pubData?.garden_drawn === true && !isMarkedNoGarden; // Original logic
    const needsDrawing = !isLoadingPub && pubData && pubData.garden_drawn === false && !isMarkedNoGarden; // Adjusted logic based on original

    const showGardenContent = !isLoadingPub && !isLoadingRaycast && pubData && raycastingData && hasDrawableGarden && gardensData.length > 0 && !error; // Uses gardensData
    const showNoGardenMessage = !isLoadingPub && pubData && isMarkedNoGarden && !error;
    const showSubmitPrompt = !isLoadingPub && !isLoadingRaycast && pubData && needsDrawing && !error; // Adjusted logic
    const showLoadingIndicator = isLoadingPub || (!isLoadingPub && pubData && !raycastingData && isLoadingRaycast && !isMarkedNoGarden && !needsDrawing && !error); // Uses needsDrawing
    const showError = !isLoadingPub && !!error;
    const showProcessingError = !isLoadingPub && !isLoadingRaycast && pubData && hasDrawableGarden && gardensData.length === 0 && !error; // Uses gardensData
    const shouldRenderSheetContent = !isLoadingPub && pubData && !error;

    // ===================
    // == Event Handlers ==
    // ===================
    // (No changes needed in event handlers for this refactor)

    const handleSubmitGarden = () => {
        if (pubData) {
            trackGardenEditStarted(pubData.google_place_id, gardensData.length);
            setSubmittedGarden(true);
            navigate('/submit', { state: { selectedPub: pubData } });
        }
    };

    const handleOpenAttribution = useCallback(() => setAttributionOpen(true), []);

    const handleOpenShareModal = () => {
        if (pubData) {
            trackShareInitiated(pubData.google_place_id, pubData.name, !!(pubData.garden_drawn && !pubData.has_no_garden));
        }
        setShareModalOpen(true);
    };

    const handleReturnToPrevious = () => {
        if (pubData) {
            timeOnPageRef.current = (Date.now() - pageLoadTimeRef.current) / 1000;
            trackReturnToSearch(pubData.google_place_id, !!(pubData.garden_drawn && !pubData.has_no_garden), timeOnPageRef.current);
        }
         // Use original navigation logic
        if (window.history.length > 2) {
             navigate(-1);
        } else {
            navigate('/'); // Fallback
        }
    };

    const getCurrentUrl = () => window.location.href;

    // Handle back button click
    const handleBackButtonClick = () => {
        // Check if we came from somewhere within the app
        if (window.history.length > 1) {
            // Navigate back
            navigate(-1);

            // Track analytics if available
            if (pubData) {
                timeOnPageRef.current = (Date.now() - pageLoadTimeRef.current) / 1000;
                trackReturnToSearch(pubData.google_place_id, !!(pubData.garden_drawn && !pubData.has_no_garden), timeOnPageRef.current);
            }
        } else {
            // If user landed directly here, go to home
            navigate('/');
        }
    };

    // == Render Functions ==
    // (No changes needed in render functions for this refactor)

    const renderShadedVertexViewer = () => {
         if (isLoadingPub || error || (!pubData) || isMarkedNoGarden || needsDrawing) return null; // Use derived boolean
        return (
             <Suspense fallback={
                 <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', p: 4, background: 'rgba(0,0,0,0.1)' }}>
                     <CircularProgress size={60} sx={{ color: themeColors.blue, mb: 2 }} />
                     <Typography variant="h6" sx={{ fontFamily: "'DM Sans', sans-serif", color: themeColors.lightBlue }}>Loading 3D map...</Typography>
                 </Box>
             }>
                 {/* Use derived boolean showGardenContent */}
                {(dynamicMeshUris.length > 0 && showGardenContent) ? (
                     <ShadedVertexViewer
                         meshUris={dynamicMeshUris}
                         initialZoom={40}
                         focalPoint={dynamicFocalPoint}
                         //markerSize={0.25}
                         gardenData={gardenDataFor3D}
                         sunVectors={raycastingData?.sun_vector}
                         height="100%"
                         //skipRaycasting={true}
                         onOpenAttribution={handleOpenAttribution}
                         bottom={isMobile ? `${PEEK_HEIGHT_NUM + 5}px` : '10px'}
                         marginLeft={isMobile ? 'auto' : '440px'}
                         gardenPolygons={raycastingData?.garden_polygons}
                     />
                 ) : (showGardenContent) ? ( // If content should show but no meshes
                      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', px: 3, background: 'rgba(0,0,0,0.1)' }}>
                         <Typography variant="h6" color="textSecondary" fontFamily="'DM Sans', sans-serif">
                             3D View Unavailable
                         </Typography>
                         <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 400 }}>
                              The 3D map data for this location could not be loaded. Sunlight timeline data below may still be available.
                         </Typography>
                     </Box>
                 ) : null }
             </Suspense>
        );
    };

    const renderCanvasOverlayContent = () => {
        // This overlay sits on top of the 3D view container
        const commonOverlayStyles = {
            position: 'absolute', top: 0, left: 0, width: '100%',
            // Cover area *above* the peeking sheet, accounting for rounded corners
            height: isMobile ? `calc(100% - ${PEEK_HEIGHT_NUM}px + 16px)` : '100%',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            textAlign: 'center', p: 3,
            background: 'rgba(255, 255, 255, 0.6)', backdropFilter: 'blur(4px)',
            zIndex: 2,
            pointerEvents: 'none', // Non-interactive by default
            boxSizing: 'border-box',
        };
         const interactiveOverlayStyles = {
             ...commonOverlayStyles,
             pointerEvents: 'auto', // Make buttons clickable
         };

        if (isLoadingPub) { // Prioritize initial pub loading message
             return (
                 <Box sx={commonOverlayStyles}>
                     <CircularProgress size={60} sx={{ mb: 2, color: themeColors.blue }} />
                     <Typography variant="h6" fontFamily="'DM Sans', sans-serif" color="text.primary">
                         Loading pub details...
                     </Typography>
                 </Box>
             );
         }
        if (showLoadingIndicator) { // Then show raycast loading
            return (
                <Box sx={commonOverlayStyles}>
                    <CircularProgress size={60} sx={{ mb: 2, color: themeColors.blue }} />
                    <Typography variant="h6" fontFamily="'DM Sans', sans-serif" color="text.primary">
                        Loading sun data...
                    </Typography>
                </Box>
            );
        }
        if (showError) {
             return (
                 <Box sx={interactiveOverlayStyles}>
                    <Typography variant="h5" color="error" gutterBottom sx={{ fontFamily: "'DM Sans', sans-serif" }}>
                        {error.includes("unavailable") ? "Data Error" : "Error"} {/* Simpler title */}
                    </Typography>
                    <Typography variant="body1" sx={{ maxWidth: 600, mb: 3 }}>
                        {error.includes("422") ? "There seems to be an issue with the submitted garden data (e.g., elevation). You can try editing it."
                        : error.includes("No gardens") ? "It looks like no gardens have been drawn for this pub yet."
                        : error.includes("find this pub") ? "Please check the URL or return to search."
                        : error // Display the error message directly
                        }
                    </Typography>
                     <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                         {/* Keep the submit button if the error is relevant */}
                         {pubData && (error.includes("No gardens") || error.includes("422")) && (
                             <SubmitGardenButton startIcon={error.includes("422") ? <EditIcon /> : <AddIcon />} onClick={handleSubmitGarden}>
                                 {error.includes("422") ? "Edit Garden for" : "Submit Garden for"} {pubData.name}
                             </SubmitGardenButton>
                         )}
                         <Button variant="contained" onClick={handleReturnToPrevious}>Back to Search</Button>
                     </Box>
                 </Box>
             );
        }
         if (showNoGardenMessage) {
             return (
                 <Box sx={interactiveOverlayStyles}>
                    <Typography variant="h5" gutterBottom sx={{ fontFamily: "'DM Sans', sans-serif", color: themeColors.darkBackground }}>
                        This pub has no garden
                    </Typography>
                    <Typography variant="body1" sx={{ maxWidth: 600, mb: 3 }}>
                        {pubData?.name} has been marked as having no garden area. If this is incorrect, you can help us map it!
                    </Typography>
                     <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                        {/* Keep the submit button */}
                        <SubmitGardenButton startIcon={<AddIcon />} onClick={handleSubmitGarden}>
                            Map Garden Area for {pubData.name}
                        </SubmitGardenButton>
                         <Button variant="contained" onClick={handleReturnToPrevious}>Back to Search</Button>
                     </Box>
                 </Box>
             );
         }
        if (showSubmitPrompt) { // Use derived boolean
             return (
                 <Box sx={interactiveOverlayStyles}>
                    <Typography variant="h6" color="textSecondary" fontFamily="'DM Sans', sans-serif">
                        Help map this pub's garden!
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 3, maxWidth: 600 }}>
                        {pubData?.name} needs its garden area drawn before sun data can be shown. Can you help outline it?
                    </Typography>
                     <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                         {/* Keep the submit button */}
                        <SubmitGardenButton startIcon={<AddIcon />} onClick={handleSubmitGarden}>
                            Map Garden for {pubData.name}
                        </SubmitGardenButton>
                         <Button variant="contained" onClick={handleReturnToPrevious}>Back to Search</Button>
                     </Box>
                 </Box>
             );
        }
         if (showProcessingError) { // Use derived boolean
              return (
                  <Box sx={interactiveOverlayStyles}>
                     <Typography variant="h6" color="textSecondary" fontFamily="'DM Sans', sans-serif">
                         Garden Data Issue
                     </Typography>
                     <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 3, maxWidth: 600 }}>
                          We couldn't process the garden data for {pubData?.name}. This might be a temporary issue, or the submitted garden might need correction.
                     </Typography>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                          {/* Keep the submit button */}
                          <SubmitGardenButton startIcon={<EditIcon />} onClick={handleSubmitGarden}>
                              Edit Garden for {pubData.name}
                          </SubmitGardenButton>
                         <Button variant="contained" onClick={handleReturnToPrevious}>Back to Search</Button>
                      </Box>
                  </Box>
              );
         }
        return null;
    };

    const ActionButtons = () => {
        return <Stack
            direction="row"
            spacing={{ xs: 1, sm: 2 }}
            justifyContent="space-around" // Distribute space
            alignItems="center"
            sx={{
                position: 'absolute', // Stick to the bottom
                bottom: 0,
                width: '100%',
                px: '10px',
                py: '20px',
                zIndex: 2, // Ensure it stays above scrollable content
                backgroundColor: 'rgba(255, 255, 255, 0.5)', // Semi-transparent background
            }}
        >
            <Button
                sx={HeaderButtonStyle}
                onClick={(e) => {
                    e.stopPropagation(); // Prevent sheet toggle on button click
                    if (pubData?.latitude && pubData?.longitude) {
                        const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(pubData.name)}&query_place_id=${pubData.google_place_id}`;
                        window.open(googleMapsUrl, '_blank', 'noopener,noreferrer');
                    }
                }}
                disabled={!pubData?.latitude || !pubData?.longitude}
                endIcon={<LocationOnIcon />}
            >
                Directions
            </Button>
            <Button
                sx={HeaderButtonStyle}
                onClick={handleOpenShareModal}
                disabled={!pubData}
                endIcon={<ShareIcon />}
            >
                Share
            </Button>
            <Button
                sx={HeaderButtonStyle}
                onClick={(e) => {
                    e.stopPropagation(); // Prevent sheet toggle on button click
                    handleSubmitGarden();
                }}
                disabled={!pubData || isLoadingPub || submittedGarden}
                endIcon={<EditIcon />}
            >
                {needsDrawing ? "Map Garden" : "Edit Garden"}
            </Button>
        </Stack>
    }

    // Shared Content Components
    const SharedContent = () => (
        <>
            {/* Garden info */}
            {showGardenContent && raycastingData?.garden_details && (
                <Box sx={{ mb: 3 }}>
                    <Box>
                        {Object.entries(raycastingData.garden_details).map(([gardenId, gardenInfo]) => (
                            <Box key={gardenId}>
                                <Paper variant="outlined" sx={{
                                    borderRadius: '12px',
                                    height: '100%',
                                    background: 'transparent',
                                    p: 2,
                                    boxShadow: 'none',
                                    border: 'none'
                                }}>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            mb: 1,
                                            color: 'black',
                                            fontWeight: 600,
                                            display: 'flex',
                                            alignItems: 'center'
                                        }}
                                    >
                                        {gardenInfo.name || `Garden ${gardenId.slice(0, 8)}`}
                                        {gardenInfo.is_uncertain &&
                                            <HelpOutlineIcon
                                                titleAccess="Garden location is uncertain"
                                                sx={{
                                                    ml: 1,
                                                    fontSize: '1.1rem',
                                                    color: themeColors.orange,
                                                    verticalAlign: 'middle'
                                                }}
                                            />
                                        }
                                    </Typography>
                                    {gardenInfo.is_uncertain &&
                                        <Typography
                                            variant="caption"
                                            display="block"
                                            sx={{
                                                mb: 1,
                                                color: themeColors.orange,
                                                fontStyle: 'italic'
                                            }}
                                        >
                                            Sun data accuracy may be affected.
                                        </Typography>
                                    }
                                    <Typography variant="body2" gutterBottom sx={{ mb: 1 }}>
                                        Today's sunshine:
                                    </Typography>
                                    {gardenHeatmaps[gardenId] ? (
                                                                            <Box sx={{
                                        background: 'transparent',
                                        backdropFilter: 'none',
                                        height: isMobile ? '100px' : '120px',
                                        width: '100%',
                                        padding: '5px',
                                        border: 'none',
                                        borderRadius: '0'
                                    }}>
                                            <IntensityTimeline
                                                data={convertToIntensityData(gardenHeatmaps[gardenId], gardenId)}
                                                googlePlaceId={gardenId}
                                                sunriseTime={raycastingData?.sunrise}
                                                sunsetTime={raycastingData?.sunset}
                                                duskDawnDurationMinutes={30}
                                                mode={timelineMode}
                                            />
                                        </Box>
                                    ) : (
                                        <Box sx={{
                                            background: 'transparent',
                                            height: isMobile ? '100px' : '120px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: muiTheme.palette.text.disabled,
                                            fontSize: '0.8rem'
                                        }}>
                                            No sunlight data
                                        </Box>
                                    )}
                                </Paper>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

            {pubData?.address && (
                <Typography variant="h6" color="text.primary" display="block" noWrap sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    mt: 0.25,
                    mb: 3,
                    pl: '16px'
                }}>
                    <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(pubData.address)}${pubData.google_place_id ? `&query_place_id=${pubData.google_place_id}` : ''}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}
                    >
                        [{pubData.address}]
                    </a>
                </Typography>
            )}

            {/* Yearly Chart */}
            {showGardenContent && (yearlySunData.length > 0 || isLoadingYearly || yearlyDataError) && (
                <Box sx={{ mb: 3 }}>
                    <YearlySunChart
                        data={yearlySunData}
                        gardenDetails={gardenSunDetails}
                        isLoading={isLoadingYearly}
                        error={yearlyDataError}
                    />
                    {isLoadingYearly && !yearlySunData.length &&
                        <Box sx={{ textAlign: 'center', my: 2 }}>
                            <CircularProgress size={30} />
                            <Typography variant="caption"> Loading yearly data...</Typography>
                        </Box>
                    }
                    {yearlyDataError && !isLoadingYearly && !yearlySunData.length &&
                        <Typography variant="caption" color="error" sx={{
                            textAlign: 'center',
                            display: 'block',
                            my: 2
                        }}>
                            {yearlyDataError}
                        </Typography>
                    }
                </Box>
            )}

            {/* AdSense */}
            {shouldRenderSheetContent && (
                <Box sx={{
                    my: 3,
                    minHeight: '90px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(0,0,0,0.03)',
                    borderRadius: '8px'
                }}>
                    <Typography variant="caption" color="text.secondary">
                        Ad Placeholder
                    </Typography>
                </Box>
            )}

            {/* Nearby Pubs */}
            {shouldRenderSheetContent && pubData && (
                <Box sx={{ mb: 3 }}>
                    <Stack direction={'row'}>
                        <Typography
                            variant="h5"
                            sx={{
                                mb: 2,
                                mr: 1,
                                fontWeight: 600,
                                fontFamily: "'DM Sans', sans-serif",
                                color: themeColors.darkBackground
                            }}
                        >
                            Nearby Pubs
                        </Typography>
                        <MouseIcon />
                    </Stack>
                    {pubData.latitude && pubData.longitude ? (
                        <NearbyPubs
                            latitude={pubData.latitude}
                            longitude={pubData.longitude}
                            currentPubId={pubData.google_place_id}
                            preloadedPubs={nearbyPubsData}
                        />
                    ) : (
                        <Box sx={{ py: 2, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                                Cannot load nearby pubs (missing coordinates)
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}

            {/* Error state buttons */}
            {(showError || showNoGardenMessage || showSubmitPrompt || showProcessingError) && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Button variant="text" onClick={handleReturnToPrevious} sx={{ color: 'text.secondary' }}>
                        Back to Search
                    </Button>
                </Box>
            )}
        </>
    );

    // Mobile-specific content wrapper
    const MobileSheetContent = () => (
        <>
            <SharedContent />
            <Box sx={{ mt: 4, mb: 4.5 }}>
                <Footer />
            </Box>
        </>
    );

    // Desktop side menu content wrapper
    const SideMenuContent = () => (
        <>
            <SharedContent />
            <Box sx={{ mt: 4, mb: 4.5 }}>
                <Footer />
            </Box>
        </>
    );


    // == Main Return JSX ==
    return (
        <>
            <Fonts />

            <Box sx={{
                height: '100vh',
                width: '100vw',
                position: 'relative',
                overflow: 'hidden',
                background: muiTheme.palette.background.default,
                boxSizing: 'border-box',
                display: 'flex', // Use flex for desktop layout
            }}>
                {/* Back Button */}
                <IconButton
                    onClick={handleBackButtonClick}
                    sx={{
                        position: 'fixed',
                        top: 16,
                        left: 16,
                        zIndex: 6,
                        backgroundColor: 'white',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                        '&:hover': {
                            backgroundColor: 'rgba(255,255,255,0.9)',
                        },
                        width: 40,
                        height: 40,
                    }}
                    aria-label="Back"
                >
                    <ArrowBackIcon sx={{ color: 'rgba(0,0,0,0.6)' }} />
                </IconButton>

                {/* Desktop Side Menu */}
                {!isMobile && (
                    <Box
                        sx={{
                            width: 400,
                            height: '100%',
                            position: 'relative',
                            zIndex: 4,
                            background: 'linear-gradient(to bottom, #FBE858 0%, #F8DAEE 100%)',
                            borderRadius: '0 16px 16px 0',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                        }}
                    >
                        {shouldRenderSheetContent && pubData && (
                            <ActionButtons />
                        )}
                        {/* Side Menu Header */}
                        <Box
                            sx={{
                                p: 3,
                                pt: 4,
                                flexShrink: 0,
                            }}
                        >

                            {/* Pub Name */}
                            <Typography
                                variant="h4"
                                sx={{
                                    fontFamily: "'DM Sans', sans-serif",
                                    fontWeight: 700,
                                    color: themeColors.darkBackground,
                                    textAlign: 'left',
                                    width: '100%',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    mt: 4,
                                    mb: 2,
                                }}
                            >
                                {pubData?.name || (isLoadingPub ? 'Loading...' : 'Pub Details')}
                            </Typography>

                            {/* Pub Details (Compact) */}
                            {shouldRenderSheetContent && pubData && (
                                <Box sx={{ width: '100%', textAlign: 'left' }}>
                                    {hasUserLocation && userDistance !== undefined && (
                                        <Typography variant="body2" color="text.primary" display="block">
                                            Distance: {userDistance >= 1000 ? `${(userDistance / 1000).toFixed(1)} km` : `${Math.round(userDistance)} m`}
                                        </Typography>
                                    )}
                                    {pubData.rating !== undefined && pubData.rating > 0 && (
                                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                                            <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>
                                                Sun Rating:
                                            </Typography>
                                            <Rating
                                                name={`rating-${pubData.google_place_id}-header`}
                                                value={pubData.rating}
                                                readOnly
                                                precision={0.5}
                                                size="small"
                                                icon={<SunIcon fontSize="inherit" sx={{ color: '#FFB400' }} />}
                                                emptyIcon={<SunIcon fontSize="inherit" sx={{ color: 'rgba(0, 0, 0, 0.26)' }} />}
                                                sx={{ '& .MuiRating-icon': { marginRight: '1px' } }}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Box>

                        {/* Side Menu Content (Scrollable) */}
                        <Box
                            sx={{
                                flex: 1,
                                overflowY: 'auto',
                                overflowX: 'hidden',
                                px: 3,
                                py: 2,
                            }}
                        >
                            {shouldRenderSheetContent && pubData && (
                                <SideMenuContent />
                            )}
                        </Box>
                    </Box>
                )}

                {/* Main Content Area (Map/3D View) */}
                <Box
                    sx={{
                        position: 'relative',
                        flex: isMobile ? 'none' : 1, // Take remaining space on desktop
                        width: isMobile ? '100%' : 'auto',
                        height: isMobile ? `calc(100% - ${PEEK_HEIGHT_NUM}px + 16px)` : '100%', // +16px to cover rounded corners
                        zIndex: 1,
                        background: '#e0e0e0',
                    }}
                >
                    {renderShadedVertexViewer()}
                    {renderCanvasOverlayContent()}
                </Box>

                {/* Mobile Bottom Sheet */}
                {isMobile && (
                    <BottomSheet
                        open={true}
                        onDismiss={() => {
                            setIsSheetExpanded(false);
                            sheetRef.current?.snapTo(({ snapPoints }) => snapPoints[0]);
                        }}
                        ref={sheetRef}
                        snapPoints={({ minHeight, maxHeight }) => [
                            PEEK_HEIGHT_NUM,
                            Math.min(maxHeight, window.innerHeight * (MAX_SHEET_HEIGHT_VH / 100))
                        ]}
                        defaultSnap={({ snapPoints }) =>
                            isSheetExpanded ? snapPoints[1] : snapPoints[0]
                        }
                        onSpringStart={(event) => {
                            if (event.type === 'SNAP' && event.source === 'user') {
                                setIsSheetExpanded(prev => !prev);
                            }
                        }}
                        blocking={false}
                        initialFocusRef={false}
                        skipInitialTransition={true}
                        expandOnContentDrag={false}
                        header={
                            <>
                                <Box
                                    onClick={() => setIsSheetExpanded(prev => !prev)}
                                    sx={{
                                        height: isSheetExpanded ? `${PEEK_HEIGHT_EXPANDED}px` : `${PEEK_HEIGHT_NUM}px`,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'flex-start',
                                        position: 'relative',
                                        cursor: 'pointer',
                                        flexShrink: 0,
                                        px: { xs: 2, sm: 3 },
                                        pt: '16px',
                                        boxSizing: 'border-box',
                                        width: '100%',
                                    }}
                                >
                                    {/* Pub Name */}
                                    <Typography
                                        variant="h3"
                                        noWrap
                                        sx={{
                                            fontFamily: "'DM Sans', sans-serif",
                                            fontWeight: 700,
                                            color: themeColors.darkBackground,
                                            textAlign: 'left',
                                            width: '100%',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            pointerEvents: 'none',
                                        }}
                                    >
                                        {pubData?.name || (isLoadingPub ? 'Loading...' : 'Pub Details')}
                                    </Typography>

                                    {/* Pub Details (Compact) */}
                                    {shouldRenderSheetContent && pubData && (
                                        <Box sx={{ width: '100%', mt: '20px', textAlign: 'left' }}>
                                            {hasUserLocation && userDistance !== undefined && (
                                                <Typography variant="caption" color="text.primary" display="block">
                                                    Distance: {userDistance >= 1000 ? `${(userDistance / 1000).toFixed(1)} km` : `${Math.round(userDistance)} m`}
                                                </Typography>
                                            )}
                                            {pubData.rating !== undefined && pubData.rating > 0 && (
                                                <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center' }}>
                                                    <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                                                        Sun Rating:
                                                    </Typography>
                                                    <Rating
                                                        name={`rating-${pubData.google_place_id}-header`}
                                                        value={pubData.rating}
                                                        readOnly
                                                        precision={0.5}
                                                        size="small"
                                                        icon={<SunIcon fontSize="inherit" sx={{ color: '#FFB400' }} />}
                                                        emptyIcon={<SunIcon fontSize="inherit" sx={{ color: 'rgba(0, 0, 0, 0.26)' }} />}
                                                        sx={{ '& .MuiRating-icon': { marginRight: '1px' } }}
                                                    />
                                                </Box>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                            </>
                        }
                        style={{
                            maxWidth: MAX_SHEET_WIDTH,
                            margin: '0 auto',
                            px: ' 0 !important',
                            zIndex: 4,
                            '--rsbs-bg': isSheetExpanded
                                ? 'linear-gradient(to bottom, #FBE858 0%, #F8DAEE 100%)'
                                : '#FBE858',
                            '--rsbs-handle-bg': 'white',
                            '--rsbs-backdrop-bg': 'rgba(0, 0, 0, 0.3)',
                            '--rsbs-border-radius': SHEET_BORDER_RADIUS,
                            '--rsbs-max-w': MAX_SHEET_WIDTH,
                            '--rsbs-ml': 'auto',
                            '--rsbs-mr': 'auto',
                            '--rsbs-content-max-height': 'none',
                            '--rsbs-transition-duration': '0.35s',
                            '--rsbs-transition-timing-function': 'cubic-bezier(0.4, 0, 0.2, 1)',
                            '--rsbs-overlay-translate-y': '0px !important',
                            '--rsbs-overlay-rounded': SHEET_BORDER_RADIUS,
                            '--rsbs-min-h': `${PEEK_HEIGHT_NUM}px`,
                        } as any}
                    >
                        {<ActionButtons />}
                        {shouldRenderSheetContent && pubData && (
                            <Box
                                sx={{
                                    px: { xs: 2, sm: 3 },
                                    pt: 1,
                                    pb: 4,
                                    minHeight: `calc(${MAX_SHEET_HEIGHT_VH}vh - ${PEEK_HEIGHT_NUM}px - 50px)`,
                                }}
                            >
                                <MobileSheetContent />
                            </Box>
                        )}
                    </BottomSheet>
                )}

                {/* Modals/Dialogs (Unchanged) */}
                <Dialog
                    open={attributionOpen}
                    onClose={() => setAttributionOpen(false)}
                    PaperProps={{ sx: { borderRadius: '12px', maxWidth: '90%', maxHeight: '80%' } }}
                >
                    <DialogTitle id="attribution-dialog-title" sx={{ fontFamily: "'DM Sans', sans-serif" }}>
                        Data Sources
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
                            3D data is provided by:
                        </Typography>
                        <List dense sx={{ maxHeight: '50vh', overflowY: 'auto' }}>
                            {isLoadingRaycast && !raycastingData?.copyrights && (
                                <ListItem>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <CircularProgress size={20} sx={{ mr: 2, color: themeColors.blue }} />
                                        <ListItemText
                                            primary="Loading attribution..."
                                            primaryTypographyProps={{ variant: 'body2', style: { fontSize: '0.9rem' } }}
                                        />
                                    </Box>
                                </ListItem>
                            )}
                            {!isLoadingRaycast && raycastingData?.copyrights && raycastingData.copyrights.map((copyright: string, index: number) => (
                                <ListItem key={index} sx={{ py: 0.5 }}>
                                    <ListItemText
                                        primary={copyright}
                                        primaryTypographyProps={{ variant: 'body2', style: { fontSize: '0.9rem' } }}
                                    />
                                </ListItem>
                            ))}
                            {!isLoadingRaycast && (!raycastingData?.copyrights || raycastingData.copyrights.length === 0) && (
                                <ListItem>
                                    <ListItemText
                                        primary="Attribution information not available"
                                        primaryTypographyProps={{ variant: 'body2', style: { fontStyle: 'italic' } }}
                                    />
                                </ListItem>
                            )}
                        </List>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setAttributionOpen(false)} variant="contained" size="small">
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>

                {pubData && (
                    <ShareModal
                        open={shareModalOpen}
                        onClose={() => setShareModalOpen(false)}
                        pubName={pubData.name}
                        pubAddress={pubData.address}
                        currentUrl={getCurrentUrl()}
                    />
                )}

                {/* JSON-LD (Unchanged) */}
                <script id="pub-json-ld" type="application/ld+json"></script>
            </Box>
        </>
    );
};

export default ViewPub;